/**
 * The deploy environment shown in the sidebar badge.
 *
 * `production` gets a deliberately different treatment from everything else:
 * the non-production badge is amber/warning, so a production tab can never be
 * misread as staging at a glance.
 */
export type EnvTone = "production" | "nonProduction";

export type EnvBadge = {
  /** Shown when the sidebar is expanded. */
  label: string;
  /** Always the raw value, so the tooltip never hides which environment this is. */
  title: string;
  tone: EnvTone;
};

/*
 * Kept short on purpose: the badge shares the brand row with the wordmark and
 * the collapse control, and a long label pushes the "Staff" wordmark into an
 * ellipsis. The untruncated value is always available as the tooltip.
 */
const MAX_LABEL = 9;

const KNOWN: Record<string, { label: string; tone: EnvTone }> = {
  production: { label: "prod", tone: "production" },
  kdev: { label: "kdev", tone: "nonProduction" },
  preview: { label: "preview", tone: "nonProduction" },
  development: { label: "dev", tone: "nonProduction" },
};

/**
 * Maps a raw `DD_RUM_ENV` value to what the badge should show. Unknown values
 * (local envs like `markwyldelocal`) are truncated so a long name cannot stretch
 * the rail, but the full value stays available as the tooltip.
 */
export function resolveEnvBadge(raw: string | undefined): EnvBadge {
  const value = (raw ?? "").trim();

  if (value === "") {
    return { label: "local", title: "local", tone: "nonProduction" };
  }

  const known = KNOWN[value.toLowerCase()];
  if (known) {
    return { ...known, title: value };
  }

  const label = value.length > MAX_LABEL ? `${value.slice(0, MAX_LABEL - 1)}…` : value;

  return { label, title: value, tone: "nonProduction" };
}
